import { useState, useEffect, useCallback } from 'react'
import PatientsFilterBar from './PatientsFilterBar'
import PatientsTable from './PatientsTable'
import AddPatientModal from './AddPatientModal'
import EmailComposerModal from './EmailComposerModal'
import { getPatients, addManualPatient, sendBulkEmail } from '../../lib/adminQueries'
import './PatientsManager.css'

function PatientsManager() {
  const [patients, setPatients] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [filters, setFilters] = useState({ minAge: null, maxAge: null, service: null })
  const [selectedEmails, setSelectedEmails] = useState([])
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEmailModal, setShowEmailModal] = useState(false)
  const [isAddingPatient, setIsAddingPatient] = useState(false)
  const [addPatientError, setAddPatientError] = useState(null)
  const [isSendingEmail, setIsSendingEmail] = useState(false)
  const [sendEmailError, setSendEmailError] = useState(null)
  const [successMessage, setSuccessMessage] = useState(null)

  const fetchPatients = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const { data, error: fetchError } = await getPatients(filters)
      if (fetchError) throw fetchError
      setPatients(data || [])
      setSelectedEmails([])
    } catch (err) {
      console.error('Error fetching patients:', err)
      setError(err.message || 'Failed to load patients')
    } finally {
      setLoading(false)
    }
  }, [filters])

  useEffect(() => {
    fetchPatients()
  }, [fetchPatients])

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters)
  }

  const handleAddPatient = async (patientData) => {
    setIsAddingPatient(true)
    setAddPatientError(null)
    try {
      const { error: addError } = await addManualPatient(patientData)
      if (addError) throw addError
      setShowAddModal(false)
      setSuccessMessage('Patient added successfully')
      setTimeout(() => setSuccessMessage(null), 3000)
      await fetchPatients()
    } catch (err) {
      console.error('Error adding patient:', err)
      setAddPatientError(err.message || 'Failed to add patient')
    } finally {
      setIsAddingPatient(false)
    }
  }

  const handleSendEmail = async (subject, body) => {
    setIsSendingEmail(true)
    setSendEmailError(null)
    try {
      const { error: sendError } = await sendBulkEmail(selectedEmails, subject, body)
      if (sendError) throw sendError
      setShowEmailModal(false)
      setSelectedEmails([])
      setSuccessMessage(`Email sent to ${selectedEmails.length} patient${selectedEmails.length !== 1 ? 's' : ''}`)
      setTimeout(() => setSuccessMessage(null), 3000)
    } catch (err) {
      console.error('Error sending email:', err)
      setSendEmailError(err.message || 'Failed to send email')
    } finally {
      setIsSendingEmail(false)
    }
  }

  return (
    <div className="patients-manager">
      <div className="manager-header">
        <h2>Patient Management</h2>
        <p>View and email patients from nominations, service requests, and manual entries</p>
      </div>

      {error && (
        <div className="error-banner">
          <p>{error}</p>
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      {successMessage && (
        <div className="success-banner">
          <p>{successMessage}</p>
          <button onClick={() => setSuccessMessage(null)}>Dismiss</button>
        </div>
      )}

      <PatientsFilterBar
        filters={filters}
        onFilterChange={handleFilterChange}
        patientCount={patients.length}
        onAddPatient={() => setShowAddModal(true)}
        onSendEmail={() => setShowEmailModal(true)}
        selectedCount={selectedEmails.length}
      />

      {loading ? (
        <div className="loading-state">Loading patients...</div>
      ) : (
        <PatientsTable
          patients={patients}
          selectedEmails={selectedEmails}
          onSelectionChange={setSelectedEmails}
        />
      )}

      {showAddModal && (
        <AddPatientModal
          onClose={() => {
            setShowAddModal(false)
            setAddPatientError(null)
          }}
          onSubmit={handleAddPatient}
          isSubmitting={isAddingPatient}
          error={addPatientError}
        />
      )}

      {showEmailModal && (
        <EmailComposerModal
          recipientCount={selectedEmails.length}
          onClose={() => {
            setShowEmailModal(false)
            setSendEmailError(null)
          }}
          onSend={handleSendEmail}
          isSending={isSendingEmail}
          error={sendEmailError}
        />
      )}
    </div>
  )
}

export default PatientsManager
